import { GetAccessDetailsRequest, GetAccessDetailsResponse } from '@upright/common/iam/access/access-details/api-types';
import { EntityTypeForAccessDetails } from '@upright/common/iam/access/access-details/types';
import {
  AddOrEditAccessesRequest,
  AddOrEditAccessesResponse,
  ChangeAccessSpaceRequest,
  DeleteAccessesRequest,
  DeleteAccessesResponse,
} from '@upright/common/iam/access/api-types';
import {
  AccessObject,
  AccessObjectId,
  AccessObjectType,
  AccessSubject,
  AccessSubjectId,
  AccessSubjectType,
  isNonWeightedEntityAccessArray,
  isWeightedEntityAccessArray,
  NonWeightedEntityAccess,
  WeightedEntityAccess,
} from '@upright/common/iam/access/types';
import { useTimeout } from '@upright/ui/common/generic/hooks/external/use-timeout';
import { get, groupBy } from 'lodash';
import pLimit from 'p-limit';
import { useEffect, useRef, useState } from 'react';
import { useMutation, UseMutationOptions, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSelector } from 'react-redux';
import {
  addOrEditAccesses,
  deleteAccess,
  fetchAccessDetails,
  fetchAccessListForSubject,
  fetchMissingAccessesOfSubjectOnObject,
  patchAccessSpace,
} from '.';
import { ErrorWithHttpStatus } from '../../common/error-with-http-status';
import { useGetVariantKey } from '../../common/hooks';
import { keysOf } from '@upright/common/utils';
import { loggedInUser } from '@upright/ui/untidy/redux/selectors';
import { removeTokenFromStorageAndForceNewLogin } from './utils';
import { clearForcedReloginMarker, hasRecentlyForcedRelogin } from './force-relogin-loop-guard';

const defaultAccessObjectTypesToFetch = [
  AccessObjectType.COMPANY,
  AccessObjectType.COMPANY_GROUP,
  AccessObjectType.PORTFOLIO,
  AccessObjectType.VARIANT_SCENARIO,
  AccessObjectType.SOURCE,
  AccessObjectType.CSRD_RESULT_ENTITY,
] as const;

type GroupedAccessQueryResponse = {
  [AccessObjectType.PORTFOLIO]: WeightedEntityAccess[];
  [AccessObjectType.COMPANY_GROUP]: WeightedEntityAccess[];
  [AccessObjectType.COMPANY]: NonWeightedEntityAccess[];
  [AccessObjectType.VARIANT_SCENARIO]: NonWeightedEntityAccess[];
  [AccessObjectType.SOURCE]: NonWeightedEntityAccess[];
  [AccessObjectType.CSRD_RESULT_ENTITY]: NonWeightedEntityAccess[];
};

// GET query for fetching the accesses. By default fetches all accesses. Given parameter
// objectTypes fetches only access objects with the given type and returns empty lists
// for other object types.
export const useGetAccessesOfSubject = (
  subject?: AccessSubject,
  objectTypes: readonly AccessObjectType[] = defaultAccessObjectTypesToFetch,
) =>
  useQuery({
    queryKey: ['accessListForSubject', subject?.subjectId, subject?.subjectType, objectTypes],

    queryFn: async () => {
      if (!subject) throw Error('Unexpectedly, query is enabled with a missing subject');
      const ret = await fetchAccessListForSubject(subject, objectTypes);
      const grouped = groupBy(ret, access => access.objectType);
      const companyAccesses = get(grouped, AccessObjectType.COMPANY, []);
      const companyGroupAccesses = get(grouped, AccessObjectType.COMPANY_GROUP, []);
      const portfolioAccesses = get(grouped, AccessObjectType.PORTFOLIO, []);
      const scenarioAccesses = get(grouped, AccessObjectType.VARIANT_SCENARIO, []);
      const sourceAccesses = get(grouped, AccessObjectType.SOURCE, []);
      const csrdAccesses = get(grouped, AccessObjectType.CSRD_RESULT_ENTITY, []);
      if (
        !(
          isNonWeightedEntityAccessArray(companyAccesses) &&
          isWeightedEntityAccessArray(companyGroupAccesses) &&
          isWeightedEntityAccessArray(portfolioAccesses) &&
          isNonWeightedEntityAccessArray(scenarioAccesses) &&
          isNonWeightedEntityAccessArray(sourceAccesses) &&
          isNonWeightedEntityAccessArray(csrdAccesses)
        )
      ) {
        throw Error('Something went wrong during access type checks.');
      }
      return {
        [AccessObjectType.COMPANY]: companyAccesses,
        [AccessObjectType.COMPANY_GROUP]: companyGroupAccesses,
        [AccessObjectType.PORTFOLIO]: portfolioAccesses,
        [AccessObjectType.VARIANT_SCENARIO]: scenarioAccesses,
        [AccessObjectType.SOURCE]: sourceAccesses,
        [AccessObjectType.CSRD_RESULT_ENTITY]: csrdAccesses,
      };
    },

    // If editing this, please also update the error in the beginning of the queryFn param above
    enabled: !!subject,

    refetchOnWindowFocus: false,
    retry: false,
  });

const missingAccessRateLimiter = pLimit(2);

export const useGetMissingAccessesOfSubjectOnObject = (
  subjectId: AccessSubjectId,
  subjectType: AccessSubjectType,
  objectId: AccessObjectId,
  objectType: AccessObjectType.COMPANY_GROUP | AccessObjectType.PORTFOLIO,
) => {
  // Delay the query on mount to avoid unnecessary server load
  const [isQueryEnabled, setQueryEnabled] = useState(false);
  useTimeout(() => {
    setQueryEnabled(true);
  }, 1000);

  return useQuery({
    queryKey: ['missingAccesses', subjectId, subjectType, objectId, objectType],

    queryFn: async () => {
      return await missingAccessRateLimiter(
        async () => await fetchMissingAccessesOfSubjectOnObject(subjectId, subjectType, objectId, objectType),
      );
    },

    enabled: isQueryEnabled,
    staleTime: 1000 * 60,
    retry: false,
    refetchOnMount: false,
  });
};

// POST mutation for removing a accesses
export const useDeleteAccessesMutation = (
  options?: UseMutationOptions<DeleteAccessesResponse, ErrorWithHttpStatus, DeleteAccessesRequest>,
) => {
  const queryClient = useQueryClient();

  const { onMutate, onSuccess, onSettled, ...optionOverrides } = options || {};
  return useMutation<DeleteAccessesResponse, ErrorWithHttpStatus, DeleteAccessesRequest>({
    mutationFn: async (request: DeleteAccessesRequest) => {
      return await deleteAccess(request);
    },
    mutationKey: ['deleteAccesses'],
    onMutate: async (request, context) => {
      if (onMutate) onMutate(request, context);
      if (request.subjectType === AccessSubjectType.USER_GROUP) {
        // Cancel any ongoing refetches
        await queryClient.cancelQueries({ queryKey: ['accessListForSubject', request.subjectId] });
      }
    },
    onSuccess: async (response, request, onMutateResult, context) => {
      if (onSuccess) await onSuccess(response, request, onMutateResult, context);
      // If relevant mutation, update the user group view query locally
      if (request.subjectType === AccessSubjectType.USER_GROUP) {
        // Get previous value from cache
        const previousAccesses = queryClient.getQueryData<GroupedAccessQueryResponse>([
          'accessListForSubject',
          request.subjectId,
        ]);
        // If there is value in cache, modify it using request info
        if (previousAccesses) {
          queryClient.setQueryData(
            ['accessListForSubject', request.subjectId],
            filterDeletedAccesses(previousAccesses, request.objects),
          );
        }
      }
    },
    onSettled: async (response, error, request, onMutateResult, context) => {
      if (onSettled) await onSettled(response, error, request, onMutateResult, context);
      // In any case, refetch afterwards
      await queryClient.invalidateQueries({ queryKey: ['accessListForSubject'] });
      await queryClient.invalidateQueries({ queryKey: ['accessListForObject'] });
    },
    ...optionOverrides,
  });
};

function filterDeletedAccesses(accesses: GroupedAccessQueryResponse, deletedObjects: AccessObject[]) {
  const filteredAccesses: Partial<typeof accesses> = {};
  for (const accessObjectType of keysOf(accesses)) {
    const accessesOfType = accesses[accessObjectType];
    const deletedObjectsIdsOfType = deletedObjects.filter(o => o.objectType === accessObjectType).map(o => o.objectId);

    const nonDeletedAccessesOfType = accessesOfType.filter(
      access => !deletedObjectsIdsOfType.includes(access.objectId),
    );
    Object.assign(filteredAccesses, { [accessObjectType]: nonDeletedAccessesOfType });
  }
  return filteredAccesses as typeof accesses;
}

// Mutation for adding or editing multiple accesses
export const useAddOrEditAccessesMutation = (
  options?: UseMutationOptions<AddOrEditAccessesResponse, ErrorWithHttpStatus, AddOrEditAccessesRequest>,
) => {
  const { onSuccess, ...optionOverrides } = options || {};
  const queryClient = useQueryClient();

  return useMutation<AddOrEditAccessesResponse, ErrorWithHttpStatus, AddOrEditAccessesRequest>({
    mutationFn: async (request: AddOrEditAccessesRequest) => {
      return await addOrEditAccesses(request);
    },
    mutationKey: ['addOrEditAccessesMutation'],
    onSuccess: async (response, request, onMutateResult, context) => {
      if (onSuccess) await onSuccess(response, request, onMutateResult, context);
      // Note: Since addition endpoint does not return new accesses together with object
      // and subject info, the local addition is difficult. This would require including
      // object and subject names with the request or as mutation parameter.
      await queryClient.invalidateQueries({ queryKey: ['accessListForSubject'] });
      await queryClient.invalidateQueries({ queryKey: ['accessListForObject'] });
    },
    ...optionOverrides,
  });
};

export const useEditAccessSpace = (onSuccess: () => void) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (request: ChangeAccessSpaceRequest) => {
      return await patchAccessSpace(request);
    },

    onSuccess: async () => {
      onSuccess();
      await queryClient.invalidateQueries({ queryKey: ['accessListForObject'] });
    },
  });
};

export function useGetAccessDetails<T extends EntityTypeForAccessDetails, TData = GetAccessDetailsResponse<T>>(params: {
  entityId?: string;
  entityType: T;
  release: string | undefined | null;
  select?: (data: GetAccessDetailsResponse<T>) => TData;
  isDisabled?: boolean;
  jobId?: number;
}) {
  const variantKeyParams = useGetVariantKey();
  const queryClient = useQueryClient();

  // Routes the request to the auth-state-specific endpoint (PLT-5177, see
  // `fetchAccessDetails`). Part of the `queryKey` so an auth-state transition
  // (login/logout) refetches from the other endpoint instead of reusing a
  // react-query entry fetched under the previous auth state.
  const viewerBelievesAuthenticated = !!useSelector(loggedInUser);

  const enabled = !!params.entityId && !params.isDisabled;

  // The query function sends `scenarioId` (from `useGetVariantKey`) and `jobId` to the server,
  // so the response depends on those too. They must be part of the `queryKey` — otherwise
  // navigating between scenarios/variants under the same `companyId` returns a stale cached
  // response (PLT-4762). `variantKeyParams` is the object returned by `useGetVariantKey`
  // (`{ scenarioId, startDate, endDate }`); react-query hashes it structurally.
  const queryKey = [
    'get-access-details',
    params.entityId,
    params.entityType,
    params.release,
    params.jobId,
    variantKeyParams,
    viewerBelievesAuthenticated,
  ];

  const query = useQuery<GetAccessDetailsResponse<T>, ErrorWithHttpStatus, TData>({
    queryKey,
    queryFn: async () => {
      const { entityId, entityType, release, jobId } = params;
      if (!entityId) throw Error(`Entity ID missing`);
      const request: GetAccessDetailsRequest<T> = {
        entityId,
        jobId,
        entityType,
        release,
        scenarioId: variantKeyParams.scenarioId,
      };
      return await fetchAccessDetails(request, viewerBelievesAuthenticated);
    },
    enabled,
    refetchOnMount: false,
    select: params.select,
  });

  // Read the raw, untransformed response from the cache for the safety net below.
  // `query.data` may have been transformed by the caller's `select`, so we cannot
  // rely on it carrying `viewerIsAuthenticated`. The component re-renders when the
  // query data changes, so reading from the cache here picks up fresh values.
  const rawData = queryClient.getQueryData<GetAccessDetailsResponse<T>>(queryKey);

  useForceReloginOnInvalidAccessDetailsSession(query.error, rawData?.viewerIsAuthenticated);

  return query;
}

/**
 * PLT-3933 safety net: when the SPA's local auth state (Redux `loggedInUser`)
 * disagrees with what the server saw on the `/api/access-details` request,
 * force the user back through the login flow instead of leaving them on a
 * stuck "logged-in but no data" page (the per-page CSRD / DMA gates fall back
 * to `shouldHide*: true` on a failed or unauthenticated access-details
 * response, which otherwise surfaces as a paywall).
 *
 * Two disagreement signals trigger the redirect:
 *
 * 1. `error.status === 401` — with the PLT-5177 endpoint split, a viewer the
 *    SPA believes to be logged in is routed to `/access-details/authenticated`
 *    (required auth), so any lapsed/invalid session surfaces as a 401 here.
 *    `fetcher-core` has already run its refresh-and-retry round before this
 *    hook sees the error, so any 401 reaching us means refresh also failed.
 *
 * 2. `viewerIsAuthenticated === false` on a 200 response while Redux still
 *    has a logged-in user (Scenario-3 mitigation). With the endpoint split
 *    this should no longer be reachable for a Redux-logged-in viewer (their
 *    requests go to the required-auth route, which 401s instead), but it is
 *    kept as defense-in-depth against any remaining path that serves a
 *    Redux-logged-in viewer an anonymous 200. Without this branch the SPA
 *    would render all data sets as inaccessible while the top bar still
 *    shows the logged-in user.
 *
 * Anonymous callers (public company pages, `/sign-in/<code>` landing) have
 * no Redux user, so neither branch fires — they keep their intended public
 * view. The `useRef` guard prevents the redirect from firing more than once
 * per mount in the rare case the query refetches before navigation
 * completes.
 *
 * The `useRef` guard only spans a single mount, but the forced relogin is a
 * full page reload that resets it. When `/api/me` and `/api/access-details`
 * persistently disagree, that reload bounces straight back into this hook — an
 * infinite redirect↔login loop. The cross-reload marker in
 * `force-relogin-loop-guard` breaks it: a second disagreement right after a
 * forced relogin is suppressed and the page renders degraded instead (PLT-5124).
 */
function useForceReloginOnInvalidAccessDetailsSession(
  error: ErrorWithHttpStatus | null,
  viewerIsAuthenticated: boolean | undefined,
) {
  const user = useSelector(loggedInUser);
  const hasForcedReloginRef = useRef(false);

  useEffect(() => {
    if (hasForcedReloginRef.current) return;
    if (!user) return;

    // Auth is healthy — clear the loop marker so a later expiry isn't mistaken
    // for a back-to-back loop.
    if (viewerIsAuthenticated === true) {
      clearForcedReloginMarker();
      return;
    }

    const sawUnauthorized = error?.status === 401;
    const serverServedAsAnonymous = viewerIsAuthenticated === false;
    if (!sawUnauthorized && !serverServedAsAnonymous) return;

    // Already forced a relogin moments ago and still disagreeing — another one
    // would just bounce the user. Stop instead of looping.
    if (hasRecentlyForcedRelogin()) {
      hasForcedReloginRef.current = true;
      return;
    }

    hasForcedReloginRef.current = true;
    removeTokenFromStorageAndForceNewLogin();
  }, [error, viewerIsAuthenticated, user]);
}
